import { env } from "@/config/env";
import { cacheGet, cacheSet } from "@/data/cache";
import { getOnlineStatus } from "@/data/connectivity";
import { enqueueMutation } from "@/data/mutation-queue";
import { getStoredToken } from "@/lib/token-store";

let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(cb: () => void) {
  onUnauthorized = cb;
}

let _expoOrigin: string | null = null;

export function setExpoOrigin(origin: string | null) {
  _expoOrigin = origin;
}

export function getExpoOrigin(): string | null {
  return _expoOrigin;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let currentToken: string | null = null;

export function setAccessToken(token: string | null) {
  currentToken = token;
}

export function getAccessToken(): string | null {
  if (currentToken) return currentToken;
  return getStoredToken();
}

const API_PREFIX = "/api";

function normalizePath(path: string): string {
  return path.startsWith("/") ? `${API_PREFIX}${path}` : `${API_PREFIX}/${path}`;
}

function extractSafeMessage(json: { message?: unknown; error?: unknown }): string {
  if (typeof json.message === "string" && json.message.length < 200) {
    return json.message.replace(/[\n\r]/g, " ").trim();
  }
  if (typeof json.error === "string" && json.error.length < 200) {
    return json.error.replace(/[\n\r]/g, " ").trim();
  }
  return "Request failed";
}

async function request<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
): Promise<{ data: T; error: null } | { data: null; error: ApiError }> {
  const apiUrl = env.apiUrl ?? (typeof window !== "undefined" ? window.location.origin : undefined);
  if (!apiUrl) {
    return { data: null, error: new ApiError(0, "API URL not configured") };
  }

  const cacheKey = `${method}:${path}`;

  if (!getOnlineStatus()) {
    if (method === "GET") {
      const cached = cacheGet<T>(cacheKey);
      if (cached) {
        return { data: cached.data, error: null };
      }
      return { data: null, error: new ApiError(0, "No connection and no cached data") };
    }

    if (method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE") {
      enqueueMutation(method as "POST" | "PUT" | "PATCH" | "DELETE", path, body);
      return { data: null as T, error: null };
    }
  }

  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const expoOrigin = getExpoOrigin();
  if (expoOrigin) headers["expo-origin"] = expoOrigin;

  try {
    const res = await fetch(`${apiUrl}${normalizePath(path)}`, {
      method,
      headers,
      credentials: "include",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const json = await res.json();

    if (!res.ok) {
      // A request started under an older token can 401 after a newer login
      // has already replaced it (e.g. a stale pre-login request landing
      // just after Google/Apple sign-in on Android, where closing the
      // Custom Tab pauses/resumes the app). Only treat the 401 as a real
      // session expiry if the token that failed is still the live one —
      // otherwise this would wipe out the freshly-set valid session.
      if (res.status === 401 && getAccessToken() === token) {
        setAccessToken(null);
        onUnauthorized?.();
      }

      return { data: null, error: new ApiError(res.status, extractSafeMessage(json)) };
    }

    const data = json.data ?? json;

    if (method === "GET") {
      cacheSet(cacheKey, data);
    }

    return { data, error: null };
  } catch (err) {
    if (method === "GET") {
      const cached = cacheGet<T>(cacheKey);
      if (cached) {
        return { data: cached.data, error: null };
      }
    }

    return {
      data: null,
      error: new ApiError(0, err instanceof Error ? err.message : "Network error"),
    };
  }
}

export const api = {
  get: <T = unknown>(path: string) => request<T>("GET", path),
  post: <T = unknown>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T = unknown>(path: string, body?: unknown) => request<T>("PUT", path, body),
  delete: <T = unknown>(path: string, body?: unknown) => request<T>("DELETE", path, body),
};

// Reads whatever `api.get(path)` last cached (including data hydrated from
// disk after a cold start) without making a request — lets a screen paint
// last-known data on mount while the real fetch happens in the background.
export function getCachedGet<T>(path: string): T | null {
  return cacheGet<T>(`GET:${path}`)?.data ?? null;
}
